import {
  createEffect,
  ofType,
  registerEffects,
  removeEffects,
  tapResult,
} from '@ngneat/effects';
import { getCurrentAuthenticatedUser } from '@tmx/auth';

import { ALK_ROLES } from '../../models/alk-roles';
import {
  AMS_ROLES,
  COMPANY_ADMIN,
  COMPANY_USER,
  READ_ONLY,
} from '../../models/ams-roles';
import { FLEET_ROLES } from '../../models/fleet-roles';
import { PARTNER_ROLES } from '../../models/partner-roles';
import { composeRoles } from '../../utils/compose-roles';
import { isViewingChildAccount } from '../account-manager/account-manager.repository';
import { accountSummarySnapshot } from '../ams/ams.repository';
import {
  appSettingsStoreSnapshot,
  companyLicenseFeaturesSnapshot,
  companySettingsSnapshot,
  isFleetPortalOnly,
  partnerSettingsSnapshot,
} from '../app-settings/app-settings.repository';
import { initializeAmsFeatures } from './ams-features.actions';
import {
  updateAmsFeatures,
  updateAmsFeaturesState,
} from './ams-features.repository';

const initializeAmsFeatures$ = createEffect((actions) =>
  actions.pipe(
    ofType(initializeAmsFeatures),
    tapResult(() => {
      updateAmsFeaturesState('loading');

      const { roles } = getCurrentAuthenticatedUser();
      const { isFleetPortalEnabled } = companySettingsSnapshot();
      const { enableAmsDiagnostics } = partnerSettingsSnapshot();
      const { companyType } = accountSummarySnapshot();
      const { weather } = companyLicenseFeaturesSnapshot();
      const { weatherAlertsEnabled } = appSettingsStoreSnapshot();

      const alkRoles = isFleetPortalOnly() ? new Set<string>() : ALK_ROLES;
      const partnerRoles = isFleetPortalOnly()
        ? new Set<string>()
        : PARTNER_ROLES;
      const amsRoles = isFleetPortalOnly() ? new Set<string>() : AMS_ROLES;

      const hasAlkRole = composeRoles(ALK_ROLES);
      const hasPartnerRole = composeRoles(PARTNER_ROLES);
      const hasCfsRole = composeRoles(ALK_ROLES, FLEET_ROLES);
      const hasAccountsRole = composeRoles(alkRoles, partnerRoles);
      const hasAssetsRole = composeRoles(alkRoles, partnerRoles, amsRoles);
      const hasViewWeatherRole = composeRoles(
        ALK_ROLES,
        PARTNER_ROLES,
        COMPANY_ADMIN,
        FLEET_ROLES,
        COMPANY_USER,
        READ_ONLY
      );
      const hasEditWeatherRole = composeRoles(
        ALK_ROLES,
        PARTNER_ROLES,
        COMPANY_ADMIN
      );

      const canEditWeather = hasEditWeatherRole(roles);

      const showAssets = hasAssetsRole(roles);

      const showCfs = isFleetPortalEnabled && hasCfsRole(roles);

      const showDashboard = companySettingsSnapshot().displayDashboard;

      const isPartnerWithDiagnosticsEnabled =
        enableAmsDiagnostics && hasPartnerRole(roles);
      const showDiagnostics =
        !isFleetPortalOnly() &&
        !isViewingChildAccount() &&
        (hasAlkRole(roles) || isPartnerWithDiagnosticsEnabled);

      const showFleetAccounts =
        companyType === 'Partner' && hasAccountsRole(roles);

      const showReporting = hasAlkRole(roles);

      const showWeather =
        weather && weatherAlertsEnabled && hasViewWeatherRole(roles);

      updateAmsFeatures({
        canEditWeather,
        showAssets,
        showCfs,
        showDashboard,
        showDiagnostics,
        showFleetAccounts,
        showManageAccount: true,
        showReporting,
        showWeather,
      });
    })
  )
);

const effects = [initializeAmsFeatures$];

export function registerAmsFeaturesEffects() {
  registerEffects(effects);
}

export function deregisterAmsFeaturesEffects() {
  removeEffects(effects);
}
